import { useState } from 'react';
import type { BankAccountType } from '@api';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { AddActionIcon } from '../AddActionIcon';
import {
  TouringCard,
  TouringCardActions,
  TouringCardHeader,
  TouringCardSection,
  TouringCardTitle,
} from '../TouringCard';
import type { BankAccountFormSchemaType } from './common/hooks';
import {
  AddBankAccountModal,
  BankAccountsTable,
  DeleteBankAccountModal,
  EditBankAccountModal,
  ViewDeletedBankAccountModal,
} from './components';

export type BankAccountsPropsType = {
  bankAccounts: BankAccountType[];
  canEdit: boolean;
  onSubmitCreate: (data: BankAccountFormSchemaType) => Promise<void>;
  onSubmitUpdate: (
    data: BankAccountFormSchemaType,
    bankAccountId: string
  ) => Promise<void>;
  onSubmitDelete: (bankAccountId: string, reason: string) => Promise<void>;
  onApprove: (bankAccountId: string) => void;
  isDeletePending: boolean;
};

export const BankAccounts: React.FC<BankAccountsPropsType> = ({
  bankAccounts,
  canEdit,
  onSubmitCreate,
  onSubmitUpdate,
  onSubmitDelete,
  onApprove,
  isDeletePending,
}) => {
  const { t } = useTranslation('common');
  const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [openedEdit, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);
  const [
    openedViewDeleted,
    { open: openViewDeletedModal, close: closeViewDeletedModal },
  ] = useDisclosure(false);
  const [editingBankAccount, setEditingBankAccount] =
    useState<BankAccountType | null>(null);
  const [viewingDeletedBankAccount, setViewingDeletedBankAccount] =
    useState<BankAccountType | null>(null);
  const [bankAccountPendingDelete, setBankAccountPendingDelete] = useState<
    string | null
  >(null);

  const handleCloseDeleteModal = (): void => {
    setBankAccountPendingDelete(null);
  };

  const handleCloseEditModal = (): void => {
    setEditingBankAccount(null);
    closeEditModal();
  };

  const handleCloseViewDeletedModal = (): void => {
    setViewingDeletedBankAccount(null);
    closeViewDeletedModal();
  };

  const handleSubmitDeleteBankAccount = async (
    reason: string
  ): Promise<void> => {
    if (!bankAccountPendingDelete) return;
    await onSubmitDelete(bankAccountPendingDelete, reason);
    handleCloseDeleteModal();
  };

  return (
    <>
      <TouringCard>
        <TouringCardHeader>
          <TouringCardTitle>{t('BANK_ACCOUNTS.TITLE')}</TouringCardTitle>
          <TouringCardActions>
            {canEdit && <AddActionIcon onClick={openModal} />}
          </TouringCardActions>
        </TouringCardHeader>
        <TouringCardSection>
          <BankAccountsTable
            bankAccounts={bankAccounts}
            onView={(account) => {
              setViewingDeletedBankAccount(account);
              openViewDeletedModal();
            }}
            onEdit={(account) => {
              setEditingBankAccount(account);
              openEditModal();
            }}
            onApprove={onApprove}
            onDelete={(account) => setBankAccountPendingDelete(account)}
            canEdit={canEdit}
          />
        </TouringCardSection>
      </TouringCard>
      <AddBankAccountModal
        opened={opened}
        onClose={closeModal}
        onSubmit={onSubmitCreate}
      />
      <DeleteBankAccountModal
        opened={bankAccountPendingDelete !== null}
        onClose={handleCloseDeleteModal}
        isPending={isDeletePending}
        onSubmitDelete={handleSubmitDeleteBankAccount}
      />
      <EditBankAccountModal
        opened={openedEdit}
        onClose={handleCloseEditModal}
        bankAccount={editingBankAccount}
        onSubmitUpdate={onSubmitUpdate}
      />
      <ViewDeletedBankAccountModal
        opened={openedViewDeleted}
        onClose={handleCloseViewDeletedModal}
        bankAccount={viewingDeletedBankAccount}
      />
    </>
  );
};
